import type { OtherParentChild } from "@/components/tree/tree-other-parent-children";
import type { LogicalTreeBounds, TreeSpatialIndex } from "@/components/tree/tree-spatial-index";
import type { MemberNode } from "@/store/tree";
import type { Edge } from "@xyflow/react";
import type { Cluster } from "@/Pages/Tenant/Distribution/tree-clustering";

export type MemberTreeLayout = {
  nodes: MemberNode[];
  edges: Edge[];
};

export type MemberTreeLayoutMetadata = {
  bounds: LogicalTreeBounds;
  otherParentChildrenByParentId: Record<number, OtherParentChild[]>;
  spatialIndex: TreeSpatialIndex;
};

export type PreparedMemberTreeLayout = MemberTreeLayout & {
  metadata: MemberTreeLayoutMetadata;
};

type CacheEntry = {
  createdAt: number;
  layout: PreparedMemberTreeLayout;
  weight: number;
};

const MAX_CACHE_AGE_MS = 60_000;
const MAX_CACHE_ENTRIES = 6;
const MAX_CACHED_NODES = 10_000;
const cache = new Map<string, CacheEntry>();
const clusterCache = new Map<string, { createdAt: number; root: Cluster }>();
let cachedNodeCount = 0;

const copyPreparedLayout = (layout: PreparedMemberTreeLayout): PreparedMemberTreeLayout => ({
  nodes: [...layout.nodes],
  edges: [...layout.edges],
  metadata: layout.metadata,
});

const removeEntry = (key: string) => {
  const entry = cache.get(key);

  if (!entry) {
    return;
  }

  cachedNodeCount -= entry.weight;
  cache.delete(key);
};

const evictExpiredEntries = () => {
  const now = Date.now();

  for (const [key, entry] of cache) {
    if (now - entry.createdAt > MAX_CACHE_AGE_MS) {
      removeEntry(key);
    }
  }
};

export const getCachedTreeLayout = (key: string): PreparedMemberTreeLayout | null => {
  evictExpiredEntries();
  const entry = cache.get(key);

  if (!entry) {
    return null;
  }

  cache.delete(key);
  cache.set(key, entry);

  return copyPreparedLayout(entry.layout);
};

export const cacheTreeLayout = (key: string, layout: PreparedMemberTreeLayout): void => {
  removeEntry(key);

  if (layout.nodes.length > MAX_CACHED_NODES) {
    return;
  }

  const entry = {
    createdAt: Date.now(),
    layout: copyPreparedLayout(layout),
    weight: layout.nodes.length,
  };

  cache.set(key, entry);
  cachedNodeCount += entry.weight;

  while (cache.size > MAX_CACHE_ENTRIES || cachedNodeCount > MAX_CACHED_NODES) {
    const oldestKey = cache.keys().next().value;

    if (typeof oldestKey !== "string") {
      break;
    }

    removeEntry(oldestKey);
  }
};

export const clearTreeLoadCache = (): void => {
  cache.clear();
  clusterCache.clear();
  cachedNodeCount = 0;
};

export const getCachedTreeCluster = (key: string): Cluster | null => {
  const entry = clusterCache.get(key);

  if (!entry || Date.now() - entry.createdAt > MAX_CACHE_AGE_MS) {
    clusterCache.delete(key);
    return null;
  }

  clusterCache.delete(key);
  clusterCache.set(key, entry);

  return structuredClone(entry.root);
};

export const cacheTreeCluster = (key: string, root: Cluster): void => {
  clusterCache.delete(key);
  clusterCache.set(key, { createdAt: Date.now(), root: structuredClone(root) });

  while (clusterCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = clusterCache.keys().next().value;

    if (typeof oldestKey !== "string") {
      break;
    }

    clusterCache.delete(oldestKey);
  }
};
